import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { useBranch } from '@/contexts/BranchContext'
import {
  dismissCommandAttention,
  fetchCommandDashboard,
  fetchCommandLive,
  mergeCommandLivePayload,
} from '@/lib/api/command'
import { apiErrorMessage } from '@/lib/apiErrorMessage'

const LIVE_POLL_MS = 20_000

/** @type {import('react').Context<CommandDashboardContextValue | null>} */
const CommandDashboardContext = createContext(null)

/**
 * @typedef {object} CommandDashboardContextValue
 * @property {Record<string, unknown> | null} data
 * @property {boolean} loading
 * @property {string} error
 * @property {() => Promise<void>} refresh
 * @property {(alertId: string) => Promise<void>} dismissAttention
 * @property {boolean} live
 */

/**
 * @param {{ children: import('react').ReactNode }} props
 */
export function CommandDashboardProvider({ children }) {
  const { activeBranchId } = useBranch()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const pollBusy = useRef(false)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const payload = await fetchCommandDashboard()
      setData(payload)
    } catch (err) {
      setData(null)
      setError(apiErrorMessage(err, 'Unable to load command dashboard.'))
    } finally {
      setLoading(false)
    }
  }, [])

  const dismissAttention = useCallback(async (alertId) => {
    await dismissCommandAttention(alertId)
    setData((prev) => {
      if (!prev || !Array.isArray(prev.attention)) return prev
      return {
        ...prev,
        attention: prev.attention.filter((item) => item.id !== alertId),
      }
    })
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh, activeBranchId])

  useEffect(() => {
    if (!data || error) return undefined

    const poll = async () => {
      if (pollBusy.current) return
      pollBusy.current = true
      try {
        const livePayload = await fetchCommandLive()
        setData((prev) => mergeCommandLivePayload(prev, livePayload))
      } catch {
        // Keep last good snapshot on transient poll failures.
      } finally {
        pollBusy.current = false
      }
    }

    const timer = window.setInterval(poll, LIVE_POLL_MS)
    return () => window.clearInterval(timer)
  }, [data, error, activeBranchId])

  const value = useMemo(
    () => ({
      data,
      loading,
      error,
      refresh,
      dismissAttention,
      live: Boolean(data) && !error,
    }),
    [data, loading, error, refresh, dismissAttention],
  )

  return (
    <CommandDashboardContext.Provider value={value}>{children}</CommandDashboardContext.Provider>
  )
}

/** @returns {CommandDashboardContextValue} */
export function useCommandDashboard() {
  const context = useContext(CommandDashboardContext)
  if (!context) {
    throw new Error('useCommandDashboard must be used within CommandDashboardProvider')
  }
  return context
}
